/**
 * TrainingInsights schema (camelCase) — matches what
 * services/background/post_training_analysis_service.py writes into
 * `trainingsessions.<id>.trainingInsights` after a session completes.
 *
 * Mirrors the post-call adherence shape where it makes sense, but DROPS the
 * fields that don't fit a training simulation:
 *   - speakerStats (AI customer talk-ratio is meaningless — it's a system output)
 *   - salesIntelligence.phases (no organic call phases in training)
 *   - closingSignal, dealRiskSignals, rapportMoments (no real deal to close)
 *
 * REPLACES the phase view with a training-appropriate progression view:
 *   - kbItemProgression — how the rep handled each KB item the AI surfaced
 */

// ---------------------------------------------------------------------------
// Per-section item interfaces
// ---------------------------------------------------------------------------

export interface ObjFaqItem {
  question: string;
  itemType: 'objection' | 'faq';
  /** ISO timestamp of when this item was handled — used to jump to the
   *  matching spot in the session's conversation timeline. */
  timestampIso?: string;
  handlingScore: number;
  adherenceLabel: 'strong' | 'partial' | 'missed' | 'harmful';
  adherenceSummary: string;
  whatWorked: string;
  whatMissed: string;
  whatRepShouldHaveSaid: string;
  keywordsUsed: string[];
  keywordsMissed: string[];
}

export interface LandmineItem {
  trigger: string;
  timestampIso?: string;
  wasTriggered: boolean;
  handlingScore: number;
  handlingSummary: string;
  whatRepSaid: string;
  recommendedResponse: string;
}

export interface CompetitorItem {
  competitorName: string;
  timestampIso?: string;
  handlingScore: number;
  handlingSummary: string;
  advantagesUsed: string[];
  advantagesMissed: string[];
  missedOpportunity: string;
  recommendedApproach: string;
}

export interface PlaybookCoverage {
  wasUsed: boolean;
  overallScore: number | null;  // null = N/A (no playbook on this track)
  overallSummary: string;
  introCoverage: {
    score: number;
    coveredPoints: string[];
    missedPoints: string[];
    coachingNote: string;
  };
  questionCoverage: {
    score: number;
    tagsCovered: string[];
    tagsMissed: string[];
    questionsCovered: string[];
    questionsMissed: string[];
    coachingNote: string;
  };
  dealProgressionCoverage: {
    score: number;
    coveredPoints: string[];
    missedPoints: string[];
    coachingNote: string;
  };
}

/** How the rep's turn on a knowledge base item ended. 'answered' and
 *  'no_answer' come from the session; 'not_attempted' marks an item the
 *  session never reached (abandoned before it was read out). */
export type KbItemStatus = 'answered' | 'no_answer' | 'not_attempted';

export interface KbItemProgression {
  trackItemId: string;
  itemType: 'objection' | 'faq' | 'landmine' | 'competitor' | string;
  question: string;
  /** Absent on sessions analysed before the read-out mode (2026-09-25):
   *  those carried a score and no status, and read as answered. */
  status?: KbItemStatus;
  /** Graded by the post-session analysis. Null for an item not attempted. */
  handlingScore: number | null;
  oneLineSummary: string;
}

export interface BuyingSignalMissed {
  signal: string;
  repResponse: string;
  suggestion: string;
}

export interface SalesIntelligence {
  buyingSignalsMissed: BuyingSignalMissed[];
  coachingForThisSession: string[];
  coachingForGeneralImprovement: string[];
  kbItemProgression: KbItemProgression[];
}

export interface ToneScore {
  /** 0-100 — how strongly this quality was present in the rep's speech. */
  score: number;
  /** 0-100 — how confident the AI is in this particular score. */
  confidenceLevel: number;
  explanation: string;
}

export interface ToneAnalysis {
  positive: {
    confidence: ToneScore;
    optimism: ToneScore;
  };
  negative: {
    defensiveness: ToneScore;
    confrontation: ToneScore;
  };
  overallCoaching: string;
}

export interface OverallCoaching {
  behavioralScore: number;
  strengths: string[];
  improvements: string[];
  scoreDimensions: Record<string, number>;
  recommendedNextTrack: string;
  managerSummary: string;
}

// ---------------------------------------------------------------------------
// Playbook Live dimensions (written only for mode === 'playbook_live')
// ---------------------------------------------------------------------------

export interface StageTiming {
  stage: string;
  label: string;
  enteredAtMs: number;
  secondsSpent: number;
  wasForced: boolean;
}

export interface StageDiscipline {
  overallScore: number | null;
  stagesReached: string[];
  stages: StageTiming[];
  discoverySeconds: number;
  skippedDiscovery: boolean;
  reachedClosing: boolean;
  earnedClosing: boolean;
  forcedStages: string[];
  overallSummary: string;
}

export interface AgendaCoverageItem {
  itemId: string;
  type: 'objection' | 'faq' | 'landmine' | 'competitor';
  /** The concern in the KB's words. Absent on sessions analysed before 2026-09-23. */
  concern?: string;
  status: 'locked' | 'unlocked' | 'raised' | 'satisfied' | 'abandoned';
  score: number | null;
  pushbackRounds: number;
  repResponse: string;
}

export interface AgendaCoverage {
  overallScore: number | null;
  totalItems: number;
  raisedCount: number;
  satisfiedCount: number;
  abandonedCount: number;
  neverRaisedCount: number;
  averageAnswerScore: number | null;
  totalPushbackRounds: number;
  items: AgendaCoverageItem[];
  overallSummary: string;
}

export interface CloseSignalResult {
  type: string;
  outcome: 'converted' | 'partial' | 'missed' | 'pending';
  emittedAtMs: number;
  repResponse: string;
}

export interface ClosingEffectiveness {
  overallScore: number | null;
  pushedForClose: boolean;
  wasCloseUnprompted: boolean;
  signalsOffered: number;
  converted: number;
  partial: number;
  missed: number;
  signals: CloseSignalResult[];
  overallSummary: string;
}

export interface ConversationalDynamics {
  repTalkSeconds: number;
  customerTalkSeconds: number;
  repTalkSharePct: number;
  overlapSeconds: number;
  repInterruptions: number;
  longestRepMonologueSeconds: number;
  hadMonologue: boolean;
  sessionSeconds: number;
  overallSummary: string;
}

export interface DiscoveryQuality {
  overallScore: number | null;
  questionsAsked: number;
  uncovered: string[];
  missed: string[];
  overallSummary: string;
}

// ---------------------------------------------------------------------------
// Top-level trainingInsights document
// ---------------------------------------------------------------------------

export type DimensionKey =
  | 'objection_faq'
  | 'landmines'
  | 'competitors'
  | 'playbook'
  | 'sales_intelligence'
  | 'tone_analysis'
  | 'summary_bullets'
  | 'overall_coaching'
  | 'stage_discipline'
  | 'agenda_coverage'
  | 'closing_effectiveness'
  | 'conversational_dynamics'
  | 'discovery_quality';

export interface AiCustomerProfile {
  /** The rep-facing briefing line; null when the track had none. */
  customerRole?: string | null;
  manner?: string;
  difficulty?: string;
  talkativeness?: string;
}

export interface TrainingInsights {
  /** not_scoreable: the analysis ran but there was nothing to grade (the rep never spoke). */
  status: 'completed' | 'pending' | 'failed' | 'not_scoreable';
  /** Why a not_scoreable session was not graded, e.g. 'no_rep_speech'. */
  reason?: string;
  /** True when the underlying training session was abandoned mid-conversation.
   *  The analyzer adapts (no penalty for items not attempted) and the UI
   *  renders a "Session Abandoned" badge. */
  wasAbandoned?: boolean;
  analyzedAt?: string;
  error?: string;

  objectionFaqHandling?: {
    overallScore: number | null;  // null = N/A (track has no items of this type)
    overallSummary: string;
    items: ObjFaqItem[];
  };
  landmineHandling?: {
    overallScore: number | null;  // null = N/A (track has no items of this type)
    overallSummary: string;
    items: LandmineItem[];
  };
  competitorHandling?: {
    overallScore: number | null;  // null = N/A (track has no items of this type)
    overallSummary: string;
    items: CompetitorItem[];
  };
  playbookCoverage?: PlaybookCoverage;
  salesIntelligence?: SalesIntelligence;
  toneAnalysis?: ToneAnalysis;
  trainingSummaryBullets?: string[];
  /** One level label per bullet, in order. Absent on older sessions. */
  trainingSummaryLabels?: string[];
  overallCoaching?: OverallCoaching;

  /** 'playbook_live' for full-duplex playbook sessions; absent for KB drills. */
  mode?: 'playbook_live';
  /** Playbook Live only: who the rep faced, snapshotted when the session was
   *  analysed. Absent on sessions analysed before it existed. */
  aiCustomer?: AiCustomerProfile;
  stageDiscipline?: StageDiscipline;
  agendaCoverage?: AgendaCoverage;
  closingEffectiveness?: ClosingEffectiveness;
  conversationalDynamics?: ConversationalDynamics;
  discoveryQuality?: DiscoveryQuality;

  /** Names of dimensions whose analysis has completed. Used to render
   *  partial progress ("5 of 8 done") during the async generation window. */
  dimensionsComplete?: DimensionKey[];
}

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

export function scoreColor(
  score: number | null | undefined,
): 'success' | 'warning' | 'error' | 'default' {
  if (score == null) return 'default';   // N/A — neutral grey
  if (score >= 80) return 'success';
  if (score >= 60) return 'warning';
  return 'error';
}

/** Render a dimension score for display: "N/A" when the score is
 *  null/undefined (the track had no items of that type), else "NN%". */
export function formatScore(score: number | null | undefined): string {
  return score == null ? 'N/A' : `${score}%`;
}

/** scoreColor variant safe for MUI <LinearProgress color>, which does
 *  NOT accept 'default'. Maps the N/A case to a neutral 'info'. */
export function barColor(
  score: number | null | undefined,
): 'success' | 'warning' | 'error' | 'info' {
  const c = scoreColor(score);
  return c === 'default' ? 'info' : c;
}
